/*
 * NetworkCoveragePanel — VA-facing dashboard for combined TP + driver
 * service-area coverage across the network, overlaid with recent trip demand.
 *
 * Purpose: help ops answer "where do we have coverage gaps?" at a glance.
 *
 * Map layers (toggleable):
 *   • TP radius circles     (forest green, semi-transparent)
 *   • Driver radius circles (brand blue,  semi-transparent)
 *   • Provider home markers (P for TP, D for driver)
 *   • Demand pickup pins    (size scales with count)
 *   • Gap ZIPs              (red rings — demand ZIPs not covered by any provider)
 *
 * Data source: GET /api/admin/network/coverage
 */
import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, CircleMarker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { apiClient, formatApiErrorDetail } from "../../lib/api";

const CENTER_DETROIT = [42.3314, -83.0458];

const homeIcon = (label, color) =>
  L.divIcon({
    className: "astat-coverage-home",
    html: `<div style="width:28px;height:28px;border-radius:50%;background:${color};color:#fff;display:grid;place-items:center;font-weight:800;font-size:12px;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.35)">${label}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });

const HOME_ICONS = {
  tp: homeIcon("P", "#2B4C3B"),
  driver: homeIcon("D", "#3A7BD5"),
};

function LegendChip({ color, label, dashed = false }) {
  return (
    <div className="flex items-center gap-2 text-[11px] text-slate_navy">
      <span
        className="inline-block w-4 h-4 rounded-full"
        style={{
          background: color,
          border: dashed ? "2px dashed #C55A44" : `2px solid ${color}`,
          opacity: dashed ? 1 : 0.7,
        }}
      />
      {label}
    </div>
  );
}

export default function NetworkCoveragePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTPs, setShowTPs] = useState(true);
  const [showDrivers, setShowDrivers] = useState(true);
  const [showDemand, setShowDemand] = useState(true);
  const [showGaps, setShowGaps] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/network/coverage");
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load coverage");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const summary = useMemo(() => {
    if (!data) return null;
    const tpsWithArea = data.tps.filter(
      (t) => t.service_area_type && (t.service_zip_codes?.length || t.service_home_lat != null)
    ).length;
    const driversWithArea = data.drivers.filter(
      (d) => d.service_area_type && (d.service_zip_codes?.length || d.service_home_lat != null)
    ).length;
    return {
      tps: data.tps.length,
      tpsWithArea,
      drivers: data.drivers.length,
      driversWithArea,
      demandZips: data.demand.length,
      gapZips: data.gaps.length,
    };
  }, [data]);

  if (loading || !data) {
    return (
      <div className="text-sm text-slate-500 py-16 text-center" data-testid="coverage-loading">
        Loading network coverage…
      </div>
    );
  }

  const center = (() => {
    const pins = [
      ...data.tps.filter((t) => t.service_home_lat != null).map((t) => [t.service_home_lat, t.service_home_lng]),
      ...data.drivers.filter((d) => d.service_home_lat != null).map((d) => [d.service_home_lat, d.service_home_lng]),
    ];
    if (!pins.length) return CENTER_DETROIT;
    const avgLat = pins.reduce((s, p) => s + p[0], 0) / pins.length;
    const avgLng = pins.reduce((s, p) => s + p[1], 0) / pins.length;
    return [avgLat, avgLng];
  })();

  return (
    <div className="space-y-4" data-testid="network-coverage-panel">
      {/* Header + KPIs */}
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate_navy">Network Coverage</h1>
          <p className="text-sm text-slate-500 mt-1">
            Combined TP + driver service areas. Red rings mark demand ZIPs no one covers — recruit or expand into these.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="coverage-refresh-btn"
          className="text-xs rounded-md border border-[#E2E0D8] hover:bg-slate-50 px-3 py-1.5 font-semibold text-slate_navy"
        >
          Refresh
        </button>
      </header>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KpiCard label="TPs" value={`${summary.tpsWithArea}/${summary.tps}`} sub="with service area" testid="kpi-tps"/>
          <KpiCard label="Drivers" value={`${summary.driversWithArea}/${summary.drivers}`} sub="with service area" testid="kpi-drivers"/>
          <KpiCard label="Demand ZIPs" value={summary.demandZips} sub="last 90 days" testid="kpi-demand"/>
          <KpiCard label="Gap ZIPs" value={summary.gapZips} sub="no provider covers" testid="kpi-gaps" red={summary.gapZips > 0}/>
        </div>
      )}

      {/* Layer toggles */}
      <div className="flex flex-wrap gap-4 rounded-md bg-[#F5F3EA] border border-[#E2E0D8] px-4 py-2 items-center">
        <LayerToggle checked={showTPs} onChange={setShowTPs} label="TP coverage" testid="toggle-tps" color="#2B4C3B"/>
        <LayerToggle checked={showDrivers} onChange={setShowDrivers} label="Driver coverage" testid="toggle-drivers" color="#3A7BD5"/>
        <LayerToggle checked={showDemand} onChange={setShowDemand} label="Demand" testid="toggle-demand" color="#B45309"/>
        <LayerToggle checked={showGaps} onChange={setShowGaps} label="Coverage gaps" testid="toggle-gaps" color="#C55A44"/>
        <div className="ml-auto flex gap-3">
          <LegendChip color="#2B4C3B" label="TP radius"/>
          <LegendChip color="#3A7BD5" label="Driver radius"/>
          <LegendChip color="#C55A44" dashed label="Gap ZIP"/>
        </div>
      </div>

      {/* Leaflet map */}
      <div className="rounded-lg overflow-hidden border border-[#E2E0D8]" style={{ height: 520 }} data-testid="coverage-map">
        <MapContainer center={center} zoom={9} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* TP radius + home markers */}
          {showTPs && data.tps.map((tp) => (
            tp.service_home_lat != null && tp.service_home_lng != null && (
              <React.Fragment key={`tp-${tp.tp_id}`}>
                {(tp.service_area_type !== "zips") && tp.service_radius_miles && (
                  <Circle
                    center={[tp.service_home_lat, tp.service_home_lng]}
                    radius={Number(tp.service_radius_miles) * 1609.34}
                    pathOptions={{ color: "#2B4C3B", fillColor: "#2B4C3B", fillOpacity: 0.10, weight: 2 }}
                  />
                )}
                <Marker position={[tp.service_home_lat, tp.service_home_lng]} icon={HOME_ICONS.tp}>
                  <Popup>
                    <div className="text-xs">
                      <div className="font-bold text-slate_navy mb-1">{tp.name}</div>
                      <div><b>Type:</b> {tp.service_area_type || "—"}</div>
                      {tp.service_radius_miles ? <div><b>Radius:</b> {tp.service_radius_miles} mi</div> : null}
                      {tp.service_zip_codes?.length ? <div><b>ZIPs:</b> {tp.service_zip_codes.join(", ")}</div> : null}
                      <div><b>Drivers:</b> {tp.driver_count}</div>
                      <div><b>Cred:</b> {tp.medexova_tp_status || "—"}</div>
                    </div>
                  </Popup>
                </Marker>
              </React.Fragment>
            )
          ))}

          {/* Driver radius + home markers */}
          {showDrivers && data.drivers.map((d) => (
            d.service_home_lat != null && d.service_home_lng != null && (
              <React.Fragment key={`drv-${d.id}`}>
                {(d.service_area_type !== "zips") && d.service_radius_miles && (
                  <Circle
                    center={[d.service_home_lat, d.service_home_lng]}
                    radius={Number(d.service_radius_miles) * 1609.34}
                    pathOptions={{ color: "#3A7BD5", fillColor: "#3A7BD5", fillOpacity: 0.08, weight: 1 }}
                  />
                )}
                <Marker position={[d.service_home_lat, d.service_home_lng]} icon={HOME_ICONS.driver}>
                  <Popup>
                    <div className="text-xs">
                      <div className="font-bold text-slate_navy mb-1">{d.name}</div>
                      {d.tp_name && <div><b>TP:</b> {d.tp_name}</div>}
                      <div><b>Type:</b> {d.service_area_type || "—"}</div>
                      {d.service_radius_miles ? <div><b>Radius:</b> {d.service_radius_miles} mi</div> : null}
                      {d.service_zip_codes?.length ? <div><b>ZIPs:</b> {d.service_zip_codes.join(", ")}</div> : null}
                      <div><b>Vehicle:</b> {d.vehicle_type || "—"}</div>
                      <div><b>Cred:</b> {d.medexova_credentialing_status || "—"}</div>
                    </div>
                  </Popup>
                </Marker>
              </React.Fragment>
            )
          ))}

          {/* Demand pickup pins (size scales with count) */}
          {showDemand && data.demand.map((r) => {
            const isGap = data.gaps.some((g) => g.zip === r.zip);
            if (isGap && showGaps) return null;  // will draw gap version below
            return (
              <CircleMarker
                key={`demand-${r.zip}`}
                center={[r.lat, r.lng]}
                radius={Math.min(6 + r.count * 2, 22)}
                pathOptions={{ color: "#B45309", fillColor: "#F59E0B", fillOpacity: 0.55, weight: 2 }}
              >
                <Popup>
                  <div className="text-xs">
                    <div className="font-bold text-slate_navy">ZIP {r.zip}</div>
                    <div><b>{r.count}</b> pickup{r.count === 1 ? "" : "s"} · last 90 days</div>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}

          {/* Gap ZIPs — highlighted red dashed rings */}
          {showGaps && data.gaps.map((g) => (
            <CircleMarker
              key={`gap-${g.zip}`}
              center={[g.lat, g.lng]}
              radius={Math.min(10 + g.count * 3, 28)}
              pathOptions={{ color: "#C55A44", fillColor: "#C55A44", fillOpacity: 0.20, weight: 3, dashArray: "6 4" }}
            >
              <Popup>
                <div className="text-xs">
                  <div className="font-bold text-red-700">GAP · ZIP {g.zip}</div>
                  <div><b>{g.count}</b> pickup{g.count === 1 ? "" : "s"} last 90 days</div>
                  <div className="text-slate-500 mt-1">No TP or driver covers this ZIP. Recruit or extend an existing provider.</div>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>

      {/* Gap list (recruiting targets) */}
      {data.gaps.length > 0 && (
        <section className="rounded-lg border border-red-200 bg-red-50 p-4" data-testid="coverage-gaps-list">
          <h2 className="font-heading text-base font-bold text-red-800 mb-2">Recruiting targets ({data.gaps.length})</h2>
          <p className="text-xs text-red-700 mb-3">
            ZIPs where riders requested pickups in the last 90 days but no MedexOVA provider covers them.
          </p>
          <ul className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
            {data.gaps.map((g) => (
              <li key={g.zip} className="bg-white border border-red-100 rounded-md px-3 py-2 text-xs" data-testid={`gap-${g.zip}`}>
                <div className="font-bold text-slate_navy">ZIP {g.zip}</div>
                <div className="text-slate-500">{g.count} pickup{g.count === 1 ? "" : "s"} last 90 days</div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub, testid, red = false }) {
  return (
    <div
      className={`rounded-lg border p-3 ${red ? "border-red-200 bg-red-50" : "border-[#E2E0D8] bg-white"}`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-500">{label}</div>
      <div className={`font-heading text-2xl font-bold mt-1 ${red ? "text-red-700" : "text-slate_navy"}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function LayerToggle({ checked, onChange, label, testid, color }) {
  return (
    <label className="inline-flex items-center gap-2 text-xs cursor-pointer text-slate_navy" data-testid={testid}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-slate-300 focus:ring-forest/40"
        style={{ accentColor: color }}
      />
      {label}
    </label>
  );
}
